import React from 'react';
import { User, Building, Edit, Key, Trash2, Eye, EyeOff, ShieldOff } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

const UserList = ({ 
  userType, 
  users, 
  loading, 
  searchTerm, 
  showPasswords, 
  onEdit, 
  onChangePassword, 
  onDelete, 
  onTogglePasswordVisibility,
  formatDate,
  mfaStatus,
  onResetMfa
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const EmptyState = ({ type, hasSearch }) => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="relative group/empty mb-6">
        <div className={`${isDarkMode ? '' : 'hidden'} absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-3xl blur-sm`}></div>
        <div className={`relative w-16 h-16 rounded-3xl flex items-center justify-center border ${isDarkMode ? 'bg-gradient-to-br from-slate-600/60 via-gray-600/50 to-slate-700/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border-white/20' : 'bg-slate-100 border-slate-200'}`}>
          {type === 'oci' ? (
            <Building className={`w-8 h-8 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-slate-500'}`} />
          ) : (
            <User className={`w-8 h-8 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-slate-500'}`} />
          )}
          <div className={`${isDarkMode ? '' : 'hidden'} absolute top-2 left-2 w-4 h-4 bg-white/30 rounded-full blur-sm`}></div>
        </div>
      </div>
      <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>
        {hasSearch ?
          t('admin.users.noUsersFound', { type: type === 'oci' ? 'OCI-' : '' }) :
          t('admin.users.noUsersExist', { type: type === 'oci' ? 'OCI-' : '' })
        }
      </h3>
      <p className={`text-center max-w-sm ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-600'}`}>
        {hasSearch ?
          t('admin.users.tryOtherSearch') :
          (type === 'oci' ? t('admin.users.createFirstOci') : t('admin.users.createFirstLocal'))
        }
      </p>
    </div>
  );

  const AdminUserCard = ({ user }) => (
    <div key={user.id} className="relative group/item">
      {/* User Card Background */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20 border border-white/20 backdrop-blur-xl shadow-[0_4px_16px_rgba(0,0,0,0.2)] group-hover/item:shadow-[0_8px_24px_rgba(0,0,0,0.3)]'
        : 'bg-white border border-slate-200 shadow-sm group-hover/item:shadow-md'
      } rounded-2xl transition-all duration-300`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10 opacity-0 group-hover/item:opacity-100'
        : ''
      } rounded-2xl transition-opacity duration-300`}></div>

      <div className="relative p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* User Icon */}
            <div className="relative group/usericon">
              <div className={`${isDarkMode ? '' : 'hidden'} absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm`}></div>
              <div className={`relative w-12 h-12 rounded-2xl flex items-center justify-center border ${isDarkMode
                ? `backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border-white/20 ${
                    user.role === 'admin' ? 'bg-gradient-to-br from-red-600/60 via-red-700/50 to-red-800/60' :
                    user.role === 'supervisor' ? 'bg-gradient-to-br from-purple-600/60 via-purple-700/50 to-purple-800/60' :
                    'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60'}`
                : `border-slate-200 ${
                    user.role === 'admin' ? 'bg-red-50' :
                    user.role === 'supervisor' ? 'bg-purple-50' :
                    'bg-brand/10'}`
              }`}>
                <User className={`w-6 h-6 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : (
                  user.role === 'admin' ? 'text-red-600' :
                  user.role === 'supervisor' ? 'text-purple-600' :
                  'text-brand'
                )}`} />
                <div className={`${isDarkMode ? '' : 'hidden'} absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm`}></div>
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h4 className={`font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{user.username}</h4>

                {/* Role Badge */}
                <span className={`relative inline-flex items-center px-3 py-1 border text-xs font-medium rounded-full ${isDarkMode ? 'backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]' : ''} ${
                  user.role === 'admin'
                    ? (isDarkMode ? 'bg-red-500/20 border-red-400/30 text-red-200' : 'bg-red-100 border-red-200 text-red-700')
                    : user.role === 'supervisor'
                      ? (isDarkMode ? 'bg-purple-500/20 border-purple-400/30 text-purple-200' : 'bg-purple-100 border-purple-200 text-purple-700')
                      : (isDarkMode ? 'bg-blue-500/20 border-blue-400/30 text-blue-200' : 'bg-brand/10 border-brand/20 text-brand')
                }`}>
                  {user.role === 'admin' ? t('admin.users.roleAdmin') :
                   user.role === 'supervisor' ? t('admin.users.roleSupervisor') : t('admin.users.rolePurchaser')}
                </span>

                {/* Konto aus Gineex Central (SSO); Support-Konten gesondert gekennzeichnet */}
                {!!user.is_gineex_support && (
                  <span className={`relative inline-flex items-center px-3 py-1 border text-xs font-medium rounded-full ${isDarkMode ? 'bg-amber-500/20 border-amber-400/30 text-amber-200' : 'bg-amber-100 border-amber-200 text-amber-700'}`}>
                    {t('header.gineexSupport')}
                  </span>
                )}
                {user.central_user_id && !user.is_gineex_support && (
                  <span className={`relative inline-flex items-center px-3 py-1 border text-xs font-medium rounded-full ${isDarkMode ? 'bg-sky-500/20 border-sky-400/30 text-sky-200' : 'bg-sky-100 border-sky-200 text-sky-700'}`}>
                    {t('users.viaCentral')}
                  </span>
                )}

                {/* 2FA-Status (nur wenn geladen) */}
                {mfaStatus && (
                  <span className={`relative inline-flex items-center px-3 py-1 border text-xs font-medium rounded-full ${
                    mfaStatus[user.id] || user.central_user_id
                      ? (isDarkMode ? 'bg-emerald-500/20 border-emerald-400/30 text-emerald-200' : 'bg-emerald-100 border-emerald-200 text-emerald-700')
                      : (isDarkMode ? 'bg-white/10 border-white/20 text-white/60' : 'bg-slate-100 border-slate-200 text-slate-500')
                  }`}>
                    {t(user.central_user_id ? 'admin.mfa.viaCentral' : mfaStatus[user.id] ? 'admin.mfa.enabled' : 'admin.mfa.disabled')}
                  </span>
                )}
              </div>

              <div className={`flex items-center space-x-4 text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                <span className={isDarkMode ? 'drop-shadow-sm' : ''}>
                  👤 {user.firstName} {user.lastName}
                </span>
                {user.email && (
                  <span className={isDarkMode ? 'drop-shadow-sm' : ''}>
                    📧 {user.email}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit(user)}
              disabled={!!user.central_user_id}
              className="relative group/edit p-2 rounded-xl transition-all duration-200 transform hover:scale-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
              title={user.central_user_id ? t('users.centralManaged') : t('admin.users.editUser')}
            >
              <div className={`absolute inset-0 rounded-xl opacity-0 group-hover/edit:opacity-100 transition-opacity duration-200 ${isDarkMode ? 'bg-gray-900/20 border border-white/20 backdrop-blur-xl' : 'bg-slate-100'}`}></div>
              <div className={`absolute inset-0 bg-blue-500/10 rounded-xl opacity-0 group-hover/edit:opacity-100 transition-opacity duration-200 ${isDarkMode ? '' : 'hidden'}`}></div>
              <Edit className={`relative w-4 h-4 transition-colors duration-200 ${isDarkMode ? 'text-white/70 group-hover/edit:text-blue-300' : 'text-slate-500 group-hover/edit:text-brand'}`} />
            </button>
            <button
              onClick={() => onChangePassword(user)}
              disabled={!!user.central_user_id}
              className="relative group/password p-2 rounded-xl transition-all duration-200 transform hover:scale-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
              title={user.central_user_id ? t('users.centralManaged') : t('admin.users.changePassword')}
            >
              <div className={`absolute inset-0 rounded-xl opacity-0 group-hover/password:opacity-100 transition-opacity duration-200 ${isDarkMode ? 'bg-gray-900/20 border border-white/20 backdrop-blur-xl' : 'bg-slate-100'}`}></div>
              <div className={`absolute inset-0 bg-yellow-500/10 rounded-xl opacity-0 group-hover/password:opacity-100 transition-opacity duration-200 ${isDarkMode ? '' : 'hidden'}`}></div>
              <Key className={`relative w-4 h-4 transition-colors duration-200 ${isDarkMode ? 'text-white/70 group-hover/password:text-yellow-300' : 'text-slate-500 group-hover/password:text-amber-600'}`} />
            </button>
            {mfaStatus?.[user.id] && (
              <button
                onClick={() => onResetMfa(user)}
                disabled={!!user.central_user_id}
                className="relative group/mfa p-2 rounded-xl transition-all duration-200 transform hover:scale-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
                title={user.central_user_id ? t('users.centralManaged') : t('admin.mfa.reset')}
              >
                <div className={`absolute inset-0 rounded-xl opacity-0 group-hover/mfa:opacity-100 transition-opacity duration-200 ${isDarkMode ? 'bg-gray-900/20 border border-white/20 backdrop-blur-xl' : 'bg-slate-100'}`}></div>
                <ShieldOff className={`relative w-4 h-4 transition-colors duration-200 ${isDarkMode ? 'text-white/70 group-hover/mfa:text-orange-300' : 'text-slate-500 group-hover/mfa:text-orange-600'}`} />
              </button>
            )}
            <button
              onClick={() => onDelete({ id: user.id, username: user.username, isOci: false })}
              disabled={!!user.central_user_id}
              className="relative group/delete p-2 rounded-xl transition-all duration-200 transform hover:scale-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
              title={user.central_user_id ? t('users.centralManaged') : t('admin.users.deleteLocalUser')}
            >
              <div className={`absolute inset-0 rounded-xl opacity-0 group-hover/delete:opacity-100 transition-opacity duration-200 ${isDarkMode ? 'bg-gray-900/20 border border-white/20 backdrop-blur-xl' : 'bg-red-50'}`}></div>
              <div className={`absolute inset-0 bg-red-500/10 rounded-xl opacity-0 group-hover/delete:opacity-100 transition-opacity duration-200 ${isDarkMode ? '' : 'hidden'}`}></div>
              <Trash2 className={`relative w-4 h-4 transition-colors duration-200 ${isDarkMode ? 'text-white/70 group-hover/delete:text-red-300' : 'text-slate-500 group-hover/delete:text-red-600'}`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const OciUserCard = ({ user }) => (
    <div key={user.id} className="relative group/item">
      {/* OCI User Card Background */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20 border border-white/20 backdrop-blur-xl shadow-[0_4px_16px_rgba(0,0,0,0.2)] group-hover/item:shadow-[0_8px_24px_rgba(0,0,0,0.3)]'
        : 'bg-white border border-slate-200 shadow-sm group-hover/item:shadow-md'
      } rounded-2xl transition-all duration-300`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10 opacity-0 group-hover/item:opacity-100'
        : ''
      } rounded-2xl transition-opacity duration-300`}></div>

      <div className="relative p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* OCI User Icon */}
            <div className="relative group/ociicon">
              <div className={`${isDarkMode ? '' : 'hidden'} absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm`}></div>
              <div className={`relative w-12 h-12 rounded-2xl flex items-center justify-center border ${isDarkMode
                ? `backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border-white/20 ${user.is_active ? 'bg-gradient-to-br from-emerald-600/60 via-emerald-700/50 to-emerald-800/60' : 'bg-gradient-to-br from-red-600/60 via-red-700/50 to-red-800/60'}`
                : `border-slate-200 ${user.is_active ? 'bg-emerald-50' : 'bg-red-50'}`
              }`}>
                <Building className={`w-6 h-6 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : (user.is_active ? 'text-emerald-600' : 'text-red-500')}`} />
                <div className={`${isDarkMode ? '' : 'hidden'} absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm`}></div>
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h4 className={`font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{user.username}</h4>

                {/* Status Badge */}
                <span className={`relative inline-flex items-center px-3 py-1 border text-xs font-medium rounded-full ${isDarkMode ? 'backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]' : ''} ${
                  user.is_active
                    ? (isDarkMode ? 'bg-emerald-500/20 border-emerald-400/30 text-emerald-200' : 'bg-emerald-100 border-emerald-200 text-emerald-700')
                    : (isDarkMode ? 'bg-red-500/20 border-red-400/30 text-red-200' : 'bg-red-100 border-red-200 text-red-700')
                }`}>
                  {user.is_active ? t('common.active') : t('common.inactive')}
                </span>
              </div>

              <div className={`flex items-center space-x-4 text-xs mb-1 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                {user.email && (
                  <span className={isDarkMode ? 'drop-shadow-sm' : ''}>
                    📧 {user.email}
                  </span>
                )}
                {user.company && (
                  <span className={isDarkMode ? 'drop-shadow-sm' : ''}>
                    🏢 {user.company}
                  </span>
                )}
              </div>

              <div className={`flex items-center space-x-4 text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                <span className={`flex items-center ${isDarkMode ? 'drop-shadow-sm' : ''}`}>
                  🔑
                  {showPasswords[user.id] ?
                    user.password :
                    '••••••••'
                  }
                  <button
                    onClick={() => onTogglePasswordVisibility(user.id)}
                    className={`ml-2 transition-colors duration-200 ${isDarkMode ? 'text-white/60 hover:text-white/90' : 'text-slate-400 hover:text-slate-700'}`}
                  >
                    {showPasswords[user.id] ?
                      <EyeOff className="w-3 h-3" /> :
                      <Eye className="w-3 h-3" />
                    }
                  </button>
                </span>
                <span className={isDarkMode ? 'drop-shadow-sm' : ''}>🕒 {t('admin.users.created')} {formatDate(user.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit(user)}
              className="relative group/edit p-2 rounded-xl transition-all duration-200 transform hover:scale-110"
              title={t('admin.users.editOci')}
            >
              <div className={`absolute inset-0 rounded-xl opacity-0 group-hover/edit:opacity-100 transition-opacity duration-200 ${isDarkMode ? 'bg-gray-900/20 border border-white/20 backdrop-blur-xl' : 'bg-slate-100'}`}></div>
              <div className={`absolute inset-0 bg-blue-500/10 rounded-xl opacity-0 group-hover/edit:opacity-100 transition-opacity duration-200 ${isDarkMode ? '' : 'hidden'}`}></div>
              <Edit className={`relative w-4 h-4 transition-colors duration-200 ${isDarkMode ? 'text-white/70 group-hover/edit:text-blue-300' : 'text-slate-500 group-hover/edit:text-brand'}`} />
            </button>
            <button
              onClick={() => onDelete({ id: user.id, username: user.username, isOci: true })}
              className="relative group/delete p-2 rounded-xl transition-all duration-200 transform hover:scale-110"
              title={t('admin.users.deleteOci')}
            >
              <div className={`absolute inset-0 rounded-xl opacity-0 group-hover/delete:opacity-100 transition-opacity duration-200 ${isDarkMode ? 'bg-gray-900/20 border border-white/20 backdrop-blur-xl' : 'bg-red-50'}`}></div>
              <div className={`absolute inset-0 bg-red-500/10 rounded-xl opacity-0 group-hover/delete:opacity-100 transition-opacity duration-200 ${isDarkMode ? '' : 'hidden'}`}></div>
              <Trash2 className={`relative w-4 h-4 transition-colors duration-200 ${isDarkMode ? 'text-white/70 group-hover/delete:text-red-300' : 'text-slate-500 group-hover/delete:text-red-600'}`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-blue-200 border-t-blue-600 mb-4"></div>
        <p className={`${isDarkMode ? 'text-white' : 'text-gray-600'} font-medium`}>
          {userType === 'oci' ? t('admin.users.ociLoading') : t('admin.users.loading')}
        </p>
      </div>
    );
  }

  if (users.length === 0) {
    return <EmptyState type={userType} hasSearch={!!searchTerm} />;
  }

  return (
    <div className="space-y-4">
      {users.map((user) => 
        userType === 'oci' ? (
          <OciUserCard key={user.id} user={user} />
        ) : (
          <AdminUserCard key={user.id} user={user} />
        )
      )}
    </div>
  );
};

export default UserList;